import axios from "../instance";
import addQuery from "../../utils/addQuery";

export default {
  // Get All with pagination and filters
  getAll: async (payload) => {
    const result = await addQuery(payload);
    return axios.api
      .get(`/product-unit-relation?${result}`)
      .then((response) => response.data);
  },

  // Get by Product ID
  getByProductId: (productId) => {
    return axios.api
      .get(`/product-unit-relation/product/${productId}`)
      .then((response) => response.data);
  },

  // Get One
  getOne: (id) => {
    return axios.api
      .get(`/product-unit-relation/${id}`)
      .then((response) => response.data);
  },

  // Search
  search: async (body, payload) => {
    const result = await addQuery(payload);
    return axios.api
      .post(`/product-unit-relation/search?${result}`, body)
      .then((response) => response.data);
  },

  // Create single relation
  create: (body) => {
    return axios.api
      .post("/product-unit-relation", body)
      .then((response) => response.data);
  },

  // Create multiple relations
  createMultiple: (body) => {
    return axios.api
      .post("/product-unit-relation/multiple", body)
      .then((response) => response.data);
  },

  // Update single relation
  update: (id, body) => {
    return axios.api
      .put(`/product-unit-relation/${id}`, body)
      .then((response) => response.data);
  },

  // Update multiple relations
  updateMultiple: (body) => {
    return axios.api
      .put("/product-unit-relation/multiple", body)
      .then((response) => response.data);
  },

  // Delete single relation
  delete: (id) => {
    return axios.api
      .delete(`/product-unit-relation/${id}`)
      .then((response) => {
        console.log("Delete API raw response:", response);
        return response.data || { success: true };
      })
      .catch((error) => {
        console.error("Delete API error:", error);
        throw error;
      });
  },

  // Delete all relations by Product ID
  deleteByProductId: (productId) => {
    return axios.api
      .delete(`/product-unit-relation/product/${productId}`)
      .then((response) => response.data);
  },

  // Convert unit quantity
  convertUnit: (body) => {
    return axios.api
      .post("/product-unit-relation/convert", body)
      .then((response) => response.data);
  },

  // Helper methods for common operations

  // Create relation with proper data structure
  createRelation: (
    productId,
    fromUnitId,
    toUnitId,
    fromQuantity,
    toQuantity,
    conversionFactor,
    price = null
  ) => {
    return this.create({
      product_id: productId,
      from_unit_id: fromUnitId,
      to_unit_id: toUnitId,
      from_quantity: fromQuantity,
      to_quantity: toQuantity,
      conversion_factor: conversionFactor,
      price: price,
    });
  },

  // Create bidirectional relations (A to B and B to A)
  createBidirectionalRelation: (
    productId,
    unitAId,
    unitBId,
    fromQuantity,
    toQuantity,
    conversionFactor,
    priceA = null,
    priceB = null
  ) => {
    return this.createMultiple({
      relations: [
        {
          product_id: productId,
          from_unit_id: unitAId,
          to_unit_id: unitBId,
          from_quantity: fromQuantity,
          to_quantity: toQuantity,
          conversion_factor: conversionFactor,
          price: priceA,
        },
        {
          product_id: productId,
          from_unit_id: unitBId,
          to_unit_id: unitAId,
          from_quantity: toQuantity,
          to_quantity: fromQuantity,
          conversion_factor: 1 / conversionFactor,
          price: priceB,
        },
      ],
    });
  },

  // Convert quantity between units
  convertQuantity: (productId, fromUnitId, toUnitId, quantity) => {
    return this.convertUnit({
      product_id: productId,
      from_unit_id: fromUnitId,
      to_unit_id: toUnitId,
      quantity: quantity,
    });
  },

  // Get relations with product and unit details
  getRelationsWithDetails: async (productId) => {
    const response = await this.getByProductId(productId);
    if (response.success && response.data) {
      // Add unit details to each relation
      const relationsWithDetails = response.data.map((relation) => ({
        ...relation,
        from_quantity: relation.from_quantity || 1,
        to_quantity: relation.to_quantity || relation.conversion_factor || 1,
        price: relation.price || null,
        fromUnit: {
          unit_name: (relation.fromUnit && relation.fromUnit.unit_name) || "",
          unit_code: (relation.fromUnit && relation.fromUnit.unit_code) || "",
        },
        toUnit: {
          unit_name: (relation.toUnit && relation.toUnit.unit_name) || "",
          unit_code: (relation.toUnit && relation.toUnit.unit_code) || "",
        },
      }));
      return {
        ...response,
        data: relationsWithDetails,
      };
    }
    return response;
  },

  // Validate relation data
  validateRelationData: (data) => {
    const errors = [];

    if (!data.product_id) errors.push("product_id is required");
    if (!data.from_unit_id) errors.push("from_unit_id is required");
    if (!data.to_unit_id) errors.push("to_unit_id is required");
    if (!data.from_quantity) errors.push("from_quantity is required");
    if (!data.to_quantity) errors.push("to_quantity is required");
    if (!data.conversion_factor || data.conversion_factor <= 0) {
      errors.push("conversion_factor must be greater than 0");
    }

    if (data.from_unit_id === data.to_unit_id) {
      errors.push("from_unit_id and to_unit_id cannot be the same");
    }

    if (data.from_quantity <= 0) {
      errors.push("from_quantity must be greater than 0");
    }

    if (data.to_quantity <= 0) {
      errors.push("to_quantity must be greater than 0");
    }

    // Validate price if provided
    if (data.price !== null && data.price !== undefined) {
      if (typeof data.price !== "number" || data.price < 0) {
        errors.push("price must be a number greater than or equal to 0");
      }
    }

    return {
      isValid: errors.length === 0,
      errors: errors,
    };
  },
};
